import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ========== AUTH ==========
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  init: () => api.post('/init'),
};

// ========== PERMISSIONS CATALOG ==========
export const permissionsAPI = {
  getCatalog: () => api.get('/permissions/catalog'),
};

// ========== USERS ==========
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ========== STAFF ==========
export const staffAPI = {
  getAll: () => api.get('/staff'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
};

// ========== CUSTOMERS ==========
export const customerAPI = {
  getAll: () => api.get('/customers'),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// ========== CORE LOADERS ==========
export const coreLoaderAPI = {
  getAll: () => api.get('/core-loaders'),
  getOne: (id) => api.get(`/core-loaders/${id}`),
  create: (data) => api.post('/core-loaders', data),
  update: (id, data) => api.put(`/core-loaders/${id}`, data),
  delete: (id) => api.delete(`/core-loaders/${id}`),
  approve: (id) => api.post(`/core-loaders/${id}/approve`),
};

// ========== WHOLE SELLERS ==========
export const wholeSellerAPI = {
  getAll: () => api.get('/whole-sellers'),
  getOne: (id) => api.get(`/whole-sellers/${id}`),
  create: (data) => api.post('/whole-sellers', data),
  update: (id, data) => api.put(`/whole-sellers/${id}`, data),
  delete: (id) => api.delete(`/whole-sellers/${id}`),
};

// ========== RATE SHEETS ==========
export const rateSheetAPI = {
  list: (params) => api.get('/rate-sheets', { params }),
  get: (id) => api.get(`/rate-sheets/${id}`),
  create: (data) => api.post('/rate-sheets', data),
  update: (id, data) => api.put(`/rate-sheets/${id}`, data),
  delete: (id) => api.delete(`/rate-sheets/${id}`),
  upload: (params, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/rate-sheets/upload', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  services: (wsId) => api.get(`/rate-sheets/services/${wsId}`),
  lookup: (params) => api.post('/rate-sheets/lookup', null, { params }),
  seedUPS: (wsId) => api.post('/rate-sheets/seed-ups', null, { params: { whole_seller_id: wsId } }),
  exportForCoreLoader: (coreLoaderId, params) =>
    `${API_BASE}/rate-sheets/export/${coreLoaderId}${params ? `?${new URLSearchParams(params).toString()}` : ''}`,
};

// ========== ZONE COUNTRIES ==========
export const zoneCountryAPI = {
  getAll: (wsId) => api.get('/zone-countries', { params: { whole_seller_id: wsId } }),
  create: (data) => api.post('/zone-countries', data),
};

// ========== CONSIGNMENTS ==========
export const consignmentAPI = {
  getAll: (params) => api.get('/consignments', { params }),
  getOne: (id) => api.get(`/consignments/${id}`),
  create: (data) => api.post('/consignments', data),
  update: (id, data) => api.put(`/consignments/${id}`, data),
  delete: (id) => api.delete(`/consignments/${id}`),
  approve: (id) => api.post(`/consignments/${id}/approve`),
  reject: (id) => api.post(`/consignments/${id}/reject`),
};

// ========== PAYMENTS ==========
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  requestDeleteCode: (id) => api.post(`/payments/${id}/delete-request`),
  delete: (id, challengeId, code) =>
    api.delete(`/payments/${id}`, { params: { challenge_id: challengeId, code } }),
};

// ========== EXPENSES ==========
export const trackingAPI = {
  updateDeliveryStatus: (id, delivery_status, note) =>
    api.patch(`/consignments/${id}/delivery-status`, { delivery_status, note }),
  trackOne: (id) => api.post(`/consignments/${id}/track`),
  refreshAll: () => api.post('/consignments/track/refresh'),
};

export const backupAPI = {
  downloadUrl: () => `${API_BASE}/backup/download`,
  snapshots: () => api.get('/backup/snapshots'),
  restore: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/backup/restore', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const expenseAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Ledger APIs
export const ledgerAPI = {
  getCustomerLedger: (id) => api.get(`/ledger/customer/${id}`),
  getCoreLoaderLedger: (id) => api.get(`/ledger/core-loader/${id}`),
  getWholeSellerLedger: (id) => api.get(`/ledger/whole-seller/${id}`),
  getVendorPartnerLedger: (id) => api.get(`/ledger/core-loader/${id}`), // legacy alias
  getVendorLedger: (id) => api.get(`/ledger/whole-seller/${id}`), // legacy alias
  getMasterLedger: (params) => api.get('/ledger/master', { params }),
  exportCustomerLedger: (id) => `${API_BASE}/ledger/customer/${id}/export`,
  exportCoreLoaderLedger: (id) => `${API_BASE}/ledger/core-loader/${id}/export`,
  exportWholeSellerLedger: (id) => `${API_BASE}/ledger/whole-seller/${id}/export`,
  exportMasterLedger: (params) => {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return `${API_BASE}/ledger/master/export${qs}`;
  },
};

// ========== REPORTS ==========
export const reportAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getProfit: (params) => api.get('/reports/profit', { params }),
  getOutstanding: (partyType, balType) => api.get('/reports/outstanding', { params: { party_type: partyType, bal_type: balType } }),
  exportOutstanding: (params) => `${API_BASE}/reports/outstanding/export?${new URLSearchParams(params).toString()}`,
};

// ========== SETTINGS ==========
export const settingsAPI = {
  getCompany: () => api.get('/settings/company'),
  updateCompany: (data, unlockToken) =>
    api.put('/settings/company', data, { params: { unlock_token: unlockToken } }),
  getPricing: () => api.get('/settings/pricing'),
  updatePricing: (data, unlockToken) =>
    api.put('/settings/pricing', data, { params: { unlock_token: unlockToken } }),
  requestUnlockCode: () => api.post('/settings/unlock/request-code'),
  unlock: (challengeId, code) =>
    api.post('/settings/unlock', null, { params: { challenge_id: challengeId, code } }),
  requestSecurityPhoneCode: () => api.post('/settings/security-phone/request-code'),
  updateCompanyVerified: (data, challengeId, code, unlockToken) =>
    api.put('/settings/company', data,
      { params: { challenge_id: challengeId, code, unlock_token: unlockToken } }),
};

// ========== INVOICES ==========
export const invoiceAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/invoices/${id}`),
  getPendingConsignments: (partyType, partyId) =>
    api.get(`/invoices/pending/${partyType}/${partyId}`),
};

// ========== IMPORT ==========
const uploadFile = (path, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(path, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const importAPI = {
  templateUrl: (entity) => `${API_BASE}/templates/${entity}`,
  importCustomers: (file) => uploadFile('/import/customers', file),
  importCoreLoaders: (file) => uploadFile('/import/core-loaders', file),
  importWholeSellers: (file) => uploadFile('/import/whole-sellers', file),
  importStaff: (file) => uploadFile('/import/staff', file),
  importConsignments: (file) => uploadFile('/import/consignments', file),
  importExpenses: (file) => uploadFile('/import/expenses', file),
};

// Legacy aliases for backward compatibility during transition
export const vendorPartnerAPI = coreLoaderAPI;
export const vendorAPI = wholeSellerAPI;
export const rateAPI = rateSheetAPI;

export default api;
